import React, { useState } from 'react';
import { MessageSquare, Send, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import { submitUserFeedback } from '../lib/api/feedback';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from './LoadingSpinner';

interface FeedbackFormProps {
  className?: string;
}

export default function FeedbackForm({ className = '' }: FeedbackFormProps) {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !feedback.trim()) return;

    setIsSubmitting(true);
    
    try {
      const { error } = await submitUserFeedback(user.id, feedback.trim(), 'dashboard');
      
      if (error) throw error;
      
      success('Feedback Sent', 'Thank you for your feedback! We appreciate your input.');
      setFeedback('');
      setIsExpanded(false);
      setHasSubmitted(true);
      
      // Reset submitted state after 5 seconds
      setTimeout(() => setHasSubmitted(false), 5000);
    } catch (err) {
      showError('Failed to Send', 'Please try again or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 rounded-xl"
        aria-expanded={isExpanded}
        aria-controls="feedback-form"
      >
        <div className="flex items-center space-x-3">
          <MessageSquare className="h-5 w-5 text-pink-400" />
          <div>
            <span className="font-medium text-gray-900">Give Feedback</span>
            {hasSubmitted && (
              <div className="flex items-center space-x-1 mt-1">
                <CheckCircle className="h-3 w-3 text-emerald-500" />
                <span className="text-xs text-emerald-600">Thank you for your feedback!</span>
              </div>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div id="feedback-form" className="px-4 pb-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="feedback-text" className="block text-sm font-medium text-gray-700 mb-2">
                How can we improve OneWin.Today?
              </label>
              <textarea
                id="feedback-text"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Share your thoughts, suggestions, or report any issues..."
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent resize-none"
                rows={4}
                maxLength={1000}
                required
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-sm text-gray-500">
                  Your feedback helps us improve the experience for everyone.
                </p>
                <span className={`text-sm ${feedback.length > 900 ? 'text-amber-600' : 'text-gray-500'}`}>
                  {feedback.length}/1000
                </span>
              </div>
            </div>

            {/* Quick feedback options */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Quick feedback:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  'Love the dashboard!',
                  'Need more customization',
                  'Social sharing is great',
                  'Want mobile app',
                  'AI coach is helpful'
                ].map((quickFeedback) => (
                  <button
                    key={quickFeedback}
                    type="button"
                    onClick={() => {
                      if (!feedback.includes(quickFeedback)) {
                        setFeedback(prev => prev ? `${prev}\n\n${quickFeedback}` : quickFeedback);
                      }
                    }}
                    className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
                  >
                    {quickFeedback}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsExpanded(false);
                  setFeedback('');
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!feedback.trim() || isSubmitting}
                className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" color="gray" />
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Send Feedback</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}